import { useEffect, useMemo, useState } from 'react';

import { CheckStatus, EntityRefUtil, GetChecksByAssetItemDTO, RecordState, WorkOrderStatus } from '@gearbelt/global';
import { useQueryClient } from '@tanstack/react-query';
import { AccessorKeyColumnDef, ColumnFiltersState, createColumnHelper, SortingState } from '@tanstack/react-table';

import SearchBar from '@/components/common/search-bar';
import { TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { Badge } from '@/components/ui/badge';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Checkbox } from '@/components/ui/checkbox';
import { DrawerTable } from '@/components/ui/drawer-table';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { columnFilterKeys } from '@/queries/column-filter';
import { formatScheduleCheckLabel } from '@/utils/schedules';

import CheckProgressBar from '../checks/check-progress-bar';
import { CheckCircleIcon } from '../icons/check-circle';
import CloseIcon from '../icons/close';
import { OpenCircleIcon } from '../icons/open-circle';
import UndoIcon from '../icons/undo';

interface WorkOrderChecksTableProps {
  checks: GetChecksByAssetItemDTO[];
  selectedCheckIds: string[];
  workOrderId?: string;
  workOrderNumber?: number;
  workOrderStatus?: WorkOrderStatus;
  isArchived: boolean;
  onToggleCheck: (checkId: string) => void;
  onToggleAll: (candidateIds: string[], allSelected: boolean) => void;
  onCheckClick: (check: GetChecksByAssetItemDTO) => void;
  hasAsset: boolean;
  loading?: boolean;
}

export function WorkOrderChecksTable({
  checks,
  selectedCheckIds,
  workOrderId,
  workOrderNumber,
  workOrderStatus,
  isArchived,
  onToggleCheck,
  onToggleAll,
  onCheckClick,
  hasAsset,
  loading,
}: WorkOrderChecksTableProps) {
  const isWorkOrderClosed = workOrderStatus === WorkOrderStatus.Closed;
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<'open' | 'closed'>('open');
  const [sorting, setSorting] = useState<{ key?: string; direction?: 'asc' | 'desc' }>({});
  const [search, setSearch] = useState('');
  const [openColumnFilters, setOpenColumnFilters] = useState<ColumnFiltersState>([]);
  const [closedColumnFilters, setClosedColumnFilters] = useState<ColumnFiltersState>(workOrderId ? [{ id: 'checkWorkOrder', value: [workOrderId] }] : []);

  const columnFilters = statusFilter === 'closed' ? closedColumnFilters : openColumnFilters;
  const setColumnFilters = statusFilter === 'closed' ? setClosedColumnFilters : setOpenColumnFilters;

  const woFilterIds = useMemo(() => {
    const activeFilters = statusFilter === 'closed' ? closedColumnFilters : openColumnFilters;
    return (activeFilters.find((f) => f.id === 'checkWorkOrder')?.value as string[] | undefined) ?? [];
  }, [statusFilter, closedColumnFilters, openColumnFilters]);
  const hasWoFilter = woFilterIds.length > 0;
  const hasNonDefaultFilter = hasWoFilter;

  const resetFilters = () => {
    if (statusFilter === 'closed') setClosedColumnFilters([]);
    else setOpenColumnFilters([]);
  };

  const woFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    const statusFiltered = checks.filter((c) => (statusFilter === 'closed' ? c.status === CheckStatus.Closed : c.status !== CheckStatus.Closed));
    for (const check of statusFiltered) {
      if (check.workOrder?.id && check.workOrder.number != null) {
        seen.set(check.workOrder.id, EntityRefUtil.createWorkOrderRef(check.workOrder.number));
      }
    }
    return Array.from(seen, ([value, label]) => ({ label, value }));
  }, [checks, statusFilter]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: columnFilterKeys.byColumn('checkWorkOrder') });
  }, [statusFilter, queryClient]);

  const filteredAndSorted = useMemo(() => {
    let filtered = [...checks];

    filtered = filtered.filter((c) => (statusFilter === 'closed' ? c.status === CheckStatus.Closed : c.status !== CheckStatus.Closed));

    if (hasWoFilter) {
      filtered = filtered.filter((c) => c.workOrder && woFilterIds.includes(c.workOrder.id));
    }

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((c) => {
        const label = (formatScheduleCheckLabel(c) ?? '').toLowerCase();
        const woRef = c.workOrder?.number != null ? EntityRefUtil.createWorkOrderRef(c.workOrder.number).toLowerCase() : '';
        return label.includes(q) || woRef.includes(q);
      });
    }

    if (sorting.key) {
      const dir = sorting.direction === 'desc' ? -1 : 1;
      filtered.sort((a, b) => {
        switch (sorting.key) {
          case 'scheduleCheck': {
            const aVal = (formatScheduleCheckLabel(a) ?? '').toLowerCase();
            const bVal = (formatScheduleCheckLabel(b) ?? '').toLowerCase();
            return dir * aVal.localeCompare(bVal);
          }
          case 'progress':
            return dir * ((a.dueProgress ?? 0) - (b.dueProgress ?? 0));
          case 'status':
            return dir * (a.status ?? '').localeCompare(b.status ?? '');
          case 'checkWorkOrder': {
            const aNum = a.workOrder?.number ?? Infinity;
            const bNum = b.workOrder?.number ?? Infinity;
            return dir * (aNum - bNum);
          }
          default:
            return 0;
        }
      });
    } else {
      filtered.sort((a, b) => {
        const progressDiff = (b.dueProgress ?? 0) - (a.dueProgress ?? 0);
        if (progressDiff !== 0) return progressDiff;
        const aVal = (formatScheduleCheckLabel(a) ?? '').toLowerCase();
        const bVal = (formatScheduleCheckLabel(b) ?? '').toLowerCase();
        return aVal.localeCompare(bVal);
      });
    }

    return filtered;
  }, [checks, statusFilter, hasWoFilter, woFilterIds, search, sorting.key, sorting.direction]);

  const sortingState: SortingState = sorting.key ? [{ id: sorting.key, desc: sorting.direction === 'desc' }] : [];

  const visibleCandidates = useMemo(() => {
    return filteredAndSorted.filter((c) => {
      const linkedToOtherWo = !!c.workOrder && c.workOrder.id !== workOrderId;
      return !linkedToOtherWo;
    });
  }, [filteredAndSorted, workOrderId]);

  const allSelected = visibleCandidates.length > 0 && visibleCandidates.every((c) => selectedCheckIds.includes(c.id));

  const handleToggleAll = () => {
    if (visibleCandidates.length === 0) return;
    onToggleAll(
      visibleCandidates.map((c) => c.id),
      allSelected,
    );
  };

  const columnHelper = createColumnHelper<GetChecksByAssetItemDTO>();

  const columns = [
    columnHelper.display({
      id: 'select',
      header: () => (
        <label className="-m-2 flex cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={allSelected}
            disabled={isWorkOrderClosed || visibleCandidates.length === 0}
            onCheckedChange={handleToggleAll}
            aria-label="Select all schedule checks"
          />
        </label>
      ),
      cell: ({ row }) => {
        const check = row.original;
        const isSelectedForThisWo = selectedCheckIds.includes(check.id);
        const linkedToOtherWo = !!check.workOrder && check.workOrder.id !== workOrderId;
        const checked = isSelectedForThisWo || linkedToOtherWo;
        return (
          <label className="-m-2 flex cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={checked} disabled={isWorkOrderClosed || linkedToOtherWo || isArchived} onCheckedChange={() => onToggleCheck(check.id)} />
          </label>
        );
      },
      meta: { cellClassName: 'w-[40px]' },
    }),
    columnHelper.accessor((c) => formatScheduleCheckLabel(c) ?? '', {
      id: 'scheduleCheck',
      header: 'Schedule Check',
      cell: ({ row }) => {
        const label = formatScheduleCheckLabel(row.original);
        return label ? <span className="block truncate">{label}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: { cellClassName: 'max-w-0' },
    }),
    columnHelper.accessor('dueProgress', {
      id: 'progress',
      header: 'Due progress',
      cell: ({ row }) => (
        <div className={cn('w-full', row.original.isUnlinked && '[&_div]:mt-0')}>
          <CheckProgressBar
            isClosed={row.original.status === CheckStatus.Closed}
            isUnlinked={row.original.isUnlinked}
            anchorType={row.original.schedule?.triggers?.[0]?.anchorType}
            hasScheduleCycle={row.original.scheduleCycleId != null}
            dueProgress={row.original.dueProgress}
            dateRemaining={row.original.dateRemaining}
            showTooltip
            allUnitProgress={row.original.allUnitProgress}
            allDateProgress={row.original.allDateProgress}
          />
        </div>
      ),
      meta: { cellClassName: 'w-[140px]' },
    }),
    columnHelper.accessor((c) => (c.status === CheckStatus.Closed ? 'Closed' : 'Open'), {
      id: 'status',
      header: 'Status',
      cell: ({ row }) =>
        row.original.status === CheckStatus.Closed ? (
          <Badge variant="success" size="sm">
            Closed
          </Badge>
        ) : (
          <Badge variant="grey" size="sm">
            Open
          </Badge>
        ),
      meta: { cellClassName: 'w-[90px]' },
    }),
    columnHelper.accessor((c) => c.workOrder?.id ?? '', {
      id: 'checkWorkOrder',
      header: 'Work Order',
      filterFn: 'arrIncludesSome',
      cell: ({ row }) => {
        const check = row.original;
        const linkedToOtherWo = !!check.workOrder && check.workOrder.id !== workOrderId;
        if (linkedToOtherWo) {
          if (!check.workOrder?.number) return <span className="text-secondary-text">-</span>;
          const ref = EntityRefUtil.createWorkOrderRef(check.workOrder.number);
          return (
            <div className="flex items-center gap-1">
              <span className="truncate">{ref}</span>
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Remove from ${ref}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleCheck(check.id);
                      }}
                      className="text-grey-5 hover:text-default-text -m-1 rounded p-1 transition-colors">
                      <CloseIcon className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">Remove from {ref}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        }
        const isAlreadyLinkedToThisWo = !!check.workOrder?.id && check.workOrder.id === workOrderId;
        const isSelected = selectedCheckIds.includes(check.id);
        const isPendingAddition = isSelected && !isAlreadyLinkedToThisWo;
        const isPendingRemoval = !isSelected && isAlreadyLinkedToThisWo;

        if (isAlreadyLinkedToThisWo && !isPendingRemoval) {
          const ref = workOrderNumber ? EntityRefUtil.createWorkOrderRef(workOrderNumber) : '-';
          return <span className="truncate">{ref}</span>;
        }
        if (isPendingAddition) {
          const ref = workOrderNumber ? EntityRefUtil.createWorkOrderRef(workOrderNumber) : 'New';
          return (
            <div className="flex items-center gap-1">
              <span className="truncate">{ref}</span>
              <div className="bg-default-green size-1.5 shrink-0 rounded-full" />
            </div>
          );
        }
        if (isPendingRemoval) {
          const ref = workOrderNumber ? EntityRefUtil.createWorkOrderRef(workOrderNumber) : '-';
          return (
            <div className="flex items-center gap-1">
              <span className="relative truncate">
                <span className="invisible">{ref}</span>
                <span className="text-secondary-text absolute inset-0">-</span>
              </span>
              <div className="bg-default-orange size-1.5 shrink-0 rounded-full" />
            </div>
          );
        }
        return <span className="text-secondary-text">-</span>;
      },
      meta: {
        cellClassName: 'w-[145px]',
        filterType: 'multi-select' as const,
        disableNullFilter: true,
        getFilterOptionsFn: async () => woFilterOptions,
      },
    }),
  ] as AccessorKeyColumnDef<GetChecksByAssetItemDTO>[];

  return (
    <div className="mb-1 flex flex-col gap-1">
      <Label>Schedule Checks</Label>
      <div className="border-grey-3 overflow-clip rounded border">
        <div className={cn('bg-grey-2 border-grey-3 flex min-h-[45px] items-center gap-2 border-b px-1', loading && 'pointer-events-none opacity-50')}>
          <ButtonStateToggle
            options={[
              { icon: <OpenCircleIcon className="h-4 w-4" />, label: 'Open', value: 'open' },
              { icon: <CheckCircleIcon className="h-4 w-4" />, label: 'Closed', value: 'closed' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <SearchBar
            search={search}
            setSearch={setSearch}
            containerClassName="max-w-[300px] flex-1"
            placeholder="Search schedule checks..."
            aria-label="Search schedule checks"
          />
          <TablePageHeaderActionButton
            type="button"
            icon={<UndoIcon className="h-4 w-4" />}
            label="Reset filters"
            kind="secondary"
            onClick={resetFilters}
            className={hasNonDefaultFilter ? '' : 'invisible'}
          />
        </div>
        {loading ? (
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-grey-1 w-[40px] p-2 text-left font-medium">
                  <Skeleton className="size-4 rounded" />
                </th>
                <th className="bg-grey-1 p-2 text-left font-medium">Schedule Check</th>
                <th className="bg-grey-1 w-[140px] p-2 text-left font-medium">Due progress</th>
                <th className="bg-grey-1 w-[90px] p-2 text-left font-medium">Status</th>
                <th className="bg-grey-1 w-[145px] p-2 text-left font-medium">Work Order</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-grey-3 border-t">
                <td className="px-2 py-1">
                  <Skeleton className="size-4 rounded" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-4 w-3/4" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-4 w-20" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-5 w-14 rounded-full" />
                </td>
                <td className="px-2 py-1">
                  <Skeleton className="h-4 w-20" />
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <DrawerTable
            data={filteredAndSorted}
            columns={columns}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            onRowClick={(row) => {
              if (row.schedule?.state !== RecordState.Deleted) {
                onCheckClick(row);
              }
            }}
            onSortingChange={setSorting}
            sorting={sortingState}
            notFoundDisplay={
              <div className="text-secondary-text py-1 text-center text-sm">
                {search || hasNonDefaultFilter ? 'No matching schedule checks' : hasAsset ? 'No schedule checks for this asset' : 'No asset selected'}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
